import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight, Filter, SlidersHorizontal, X } from "lucide-react";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import { useCategories, useFacets, useProducts } from "@/hooks/useCatalog";
import { useStore } from "@/context/StoreContext";
import { formatPrice } from "@/lib/format";
import { useSeo } from "@/lib/seo";

const SORTS = [
  { value: "popular", label: "Popularité" },
  { value: "rating", label: "Meilleures notes" },
  { value: "price_asc", label: "Prix croissant" },
  { value: "price_desc", label: "Prix décroissant" },
  { value: "discount", label: "Meilleures réductions" },
  { value: "new", label: "Nouveautés" },
  { value: "name", label: "Nom (A-Z)" },
];

const PLATFORM_LABELS = {
  windows: "Windows",
  mac: "macOS",
  multi: "Multi-plateformes",
  android: "Android",
  ios: "iPhone / iOS",
  web: "Web",
};

function FilterBlock({ title, children }) {
  return (
    <div className="border-b border-line py-5 first:pt-0 last:border-0">
      <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">{title}</p>
      {children}
    </div>
  );
}

function CheckLine({ label, checked, onChange, testId }) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 py-1.5 text-sm text-ink">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
      />
      <span className="flex-1">{label}</span>
    </label>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const { data: categories = [] } = useCategories();
  const { currency } = useStore();
  const [showFilters, setShowFilters] = useState(false);

  const category = params.get("categorie") || "";
  const q = params.get("q") || "";
  const sort = params.get("tri") || "popular";
  const page = Number(params.get("page") || 1);
  const platform = params.get("plateforme") || "";
  const licenseType = params.get("licence") || "";
  const duration = params.get("duree") || "";
  const devices = params.get("appareils") || "";
  const minPrice = params.get("prix_min") || "";
  const maxPrice = params.get("prix_max") || "";
  const inStock = params.get("stock") === "1";
  const onlyPromo = params.get("promo") === "1";

  const { data: facets } = useFacets(category);
  const queryParams = useMemo(
    () => ({
      category,
      q,
      sort,
      page,
      platform,
      license_type: licenseType,
      duration,
      devices: devices || 0,
      min_price: minPrice || 0,
      max_price: maxPrice || 0,
      in_stock: inStock,
      promo: onlyPromo,
      limit: 24,
    }),
    [category, q, sort, page, platform, licenseType, duration, devices, minPrice, maxPrice, inStock, onlyPromo],
  );
  const { data, isLoading } = useProducts(queryParams);

  const activeCategory = categories.find((c) => c.slug === category);
  const title = activeCategory ? `${activeCategory.name} — ISMO-TECH` : "Boutique — ISMO-TECH";

  useSeo({
    title,
    description: activeCategory
      ? `${activeCategory.description} Achetez vos licences ${activeCategory.name} chez ISMO-TECH avec livraison instantanée.`
      : "Tout le catalogue ISMO-TECH : licences Windows, Office, antivirus, Adobe, outils, déblocage et abonnements.",
  });

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next);
  };

  const toggleMulti = (key, value, current) => {
    const list = current ? current.split(",") : [];
    const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
    update(key, next.join(","));
  };

  const clearAll = () => setParams(new URLSearchParams(q ? { q } : {}));

  const activeCount = [category, platform, licenseType, duration, devices, minPrice, maxPrice, inStock, onlyPromo]
    .filter(Boolean).length;

  const filters = (
    <div>
      <FilterBlock title="Catégories">
        <div className="space-y-0.5">
          {categories.map((cat) => (
            <CheckLine
              key={cat.slug}
              label={cat.name}
              testId={`filter-category-${cat.slug}`}
              checked={category.split(",").includes(cat.slug)}
              onChange={() => toggleMulti("categorie", cat.slug, category)}
            />
          ))}
        </div>
      </FilterBlock>

      <FilterBlock title="Prix">
        <div className="flex items-center gap-2">
          <input
            type="number"
            data-testid="filter-min-price"
            placeholder="Min"
            value={minPrice}
            onChange={(e) => update("prix_min", e.target.value)}
            className="h-10 w-full rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none"
          />
          <span className="text-slate-400">—</span>
          <input
            type="number"
            data-testid="filter-max-price"
            placeholder="Max"
            value={maxPrice}
            onChange={(e) => update("prix_max", e.target.value)}
            className="h-10 w-full rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        {facets && (
          <p className="mt-2 text-[11px] text-slate-400">
            De {formatPrice(facets.min_price, currency)} à {formatPrice(facets.max_price, currency)}
          </p>
        )}
      </FilterBlock>

      <FilterBlock title="Système">
        <div className="space-y-0.5">
          {(facets?.platforms || []).map((p) => (
            <CheckLine
              key={p}
              label={PLATFORM_LABELS[p] || p}
              testId={`filter-platform-${p}`}
              checked={platform.split(",").includes(p)}
              onChange={() => toggleMulti("plateforme", p, platform)}
            />
          ))}
        </div>
      </FilterBlock>

      <FilterBlock title="Type de licence">
        <div className="space-y-0.5">
          {(facets?.license_types || []).map((l) => (
            <CheckLine
              key={l}
              label={l}
              testId={`filter-license-${l.replace(/[^a-zA-Z]/g, "")}`}
              checked={licenseType.split(",").includes(l)}
              onChange={() => toggleMulti("licence", l, licenseType)}
            />
          ))}
        </div>
      </FilterBlock>

      <FilterBlock title="Durée">
        <div className="space-y-0.5">
          {(facets?.durations || []).map((d) => (
            <CheckLine
              key={d}
              label={d}
              testId={`filter-duration-${d.replace(/[^a-zA-Z0-9]/g, "")}`}
              checked={duration.split(",").includes(d)}
              onChange={() => toggleMulti("duree", d, duration)}
            />
          ))}
        </div>
      </FilterBlock>

      <FilterBlock title="Nombre d'appareils">
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 5, 6].map((n) => (
            <button
              key={n}
              data-testid={`filter-devices-${n}`}
              onClick={() => update("appareils", devices === String(n) ? "" : String(n))}
              className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors duration-200 ${
                devices === String(n) ? "border-brand bg-brand text-white" : "border-line text-ink hover:border-brand"
              }`}
            >
              {n}+
            </button>
          ))}
        </div>
      </FilterBlock>

      <FilterBlock title="Disponibilité">
        <CheckLine
          label="En stock uniquement"
          testId="filter-in-stock"
          checked={inStock}
          onChange={() => update("stock", inStock ? "" : "1")}
        />
        <CheckLine
          label="En promotion"
          testId="filter-promo"
          checked={onlyPromo}
          onChange={() => update("promo", onlyPromo ? "" : "1")}
        />
      </FilterBlock>
    </div>
  );

  return (
    <div className="container-it py-6 lg:py-10">
      <nav className="mb-5 flex items-center gap-1.5 text-xs text-slate-500">
        <Link to="/" className="hover:text-brand">Accueil</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/boutique" className="hover:text-brand">Boutique</Link>
        {activeCategory && (
          <>
            <ChevronRight className="h-3 w-3" />
            <span className="font-semibold text-ink">{activeCategory.name}</span>
          </>
        )}
      </nav>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink sm:text-4xl" data-testid="shop-title">
            {activeCategory ? activeCategory.name : q ? `Résultats pour « ${q} »` : "Toute la boutique"}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500" data-testid="shop-result-count">
            {data?.total ?? 0} produit{(data?.total ?? 0) > 1 ? "s" : ""} disponible
            {(data?.total ?? 0) > 1 ? "s" : ""}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="mobile-filters-btn"
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-2 rounded-full border border-line px-4 py-2.5 text-sm font-bold text-ink lg:hidden"
          >
            <Filter className="h-4 w-4" />
            Filtres
            {activeCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1 text-[10px] text-white">
                {activeCount}
              </span>
            )}
          </button>
          <div className="relative">
            <select
              data-testid="shop-sort-select"
              value={sort}
              onChange={(e) => update("tri", e.target.value)}
              className="h-11 appearance-none rounded-full border border-line bg-white pl-4 pr-10 text-sm font-semibold text-ink focus:border-brand focus:outline-none"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <SlidersHorizontal className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-[140px] rounded-2xl border border-line bg-white p-5">
            <div className="mb-2 flex items-center justify-between">
              <p className="font-display text-base font-bold text-ink">Filtres</p>
              {activeCount > 0 && (
                <button
                  data-testid="clear-filters-btn"
                  onClick={clearAll}
                  className="text-xs font-bold text-brand hover:underline"
                >
                  Réinitialiser
                </button>
              )}
            </div>
            {filters}
          </div>
        </aside>

        <div>
          {isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
              {Array.from({ length: 9 }).map((_, i) => <ProductCardSkeleton key={i} />)}
            </div>
          ) : data?.items?.length ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3" data-testid="shop-product-grid">
                {data.items.map((product, i) => (
                  <ProductCard key={product.id} product={product} index={i} />
                ))}
              </div>

              {data.pages > 1 && (
                <div className="mt-10 flex items-center justify-center gap-2">
                  {Array.from({ length: data.pages }).map((_, i) => (
                    <button
                      key={i}
                      data-testid={`shop-page-${i + 1}`}
                      onClick={() => update("page", String(i + 1))}
                      className={`h-10 w-10 rounded-full text-sm font-bold transition-colors duration-200 ${
                        page === i + 1 ? "bg-brand text-white" : "border border-line text-ink hover:border-brand"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-line py-20 text-center" data-testid="shop-empty">
              <p className="font-display text-lg font-bold text-ink">Aucun produit trouvé</p>
              <p className="mt-2 text-sm text-slate-500">Essayez d'élargir vos filtres ou une autre recherche.</p>
              <button
                onClick={clearAll}
                className="mt-5 rounded-full bg-brand px-6 py-2.5 text-sm font-bold text-white"
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-[90] lg:hidden" data-testid="mobile-filters-panel">
          <div className="absolute inset-0 bg-ink/50" onClick={() => setShowFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[86vh] overflow-y-auto rounded-t-3xl bg-white p-5 pb-24">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-display text-lg font-bold text-ink">Filtres</p>
              <button
                data-testid="mobile-filters-close"
                onClick={() => setShowFilters(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {filters}
            <div className="fixed bottom-0 left-0 right-0 flex gap-3 border-t border-line bg-white p-4">
              <button
                onClick={clearAll}
                className="flex-1 rounded-full border border-line py-3 text-sm font-bold text-ink"
              >
                Réinitialiser
              </button>
              <button
                data-testid="mobile-filters-apply"
                onClick={() => setShowFilters(false)}
                className="flex-1 rounded-full bg-brand py-3 text-sm font-bold text-white"
              >
                Voir {data?.total ?? 0} produits
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
